import AlternativesQueries from '@Alternatives/Alternatives/Domain/queries-vue-component';
import { ImportAlternativesPageReadModel } from '@Alternatives/Alternatives/Domain/readmodels';
import { DownloadEmptyAlternativesSpreadsheetCommandV1, ImportAlternativesFromSpreadsheetCommandV1 } from '@Alternatives/AlternativesLists/Domain/commands-core';
import AlternativesListsCommands from '@Alternatives/AlternativesLists/Domain/commands-vue-component';
import { AttributeEditorDialogViewModel } from '@Alternatives/Attributes/Components/AttributeEditorDialog';
import AttributeEditorDialog from '@Alternatives/Attributes/Components/AttributeEditorDialog.vue';
import AttributesQueries from '@Alternatives/Attributes/Domain/queries-vue-component';
import { AttributeEditorReadModel, AttributeReadModel } from '@Alternatives/Attributes/Domain/readmodels';
import { SpreadsheetColumnToAlternativePropertyMapping } from '@Alternatives/Model';
import FileUploader from '@Blobs/Components/FileUploader.vue';
import { strCompareLowerCase } from '@common/Common';
import PortfolioOrDecisionNavigation from "@common/Components/PortfolioOrDecisionNavigation.vue";
import { ValidationError } from '@common/types';
import { Component, mixins } from 'vue-facing-decorator';

@Component({ components: { PortfolioOrDecisionNavigation, FileUploader, AttributeEditorDialog } })
export default class AlternativesImportPage extends mixins(AlternativesQueries, AlternativesListsCommands, AttributesQueries)
{
    get workspaceId(): number { return Number(this.$route?.params?.workspaceId ?? 0); }
    get projectId(): number { return Number(this.$route?.params?.projectId ?? 0); }
    get alternativesListId(): number { return Number(this.$route?.params?.alternativesListId ?? 0); }


    rm: ImportAlternativesPageReadModel = null;
    file: File = null;
    blobId: string = null;
    importResult: ImportAlternativesFromSpreadsheetResult = null;

    ImportStep: string = "source";
    ImportCompleted: boolean = false;
    Processing: boolean = false;
    UpdateExisting: boolean = false;

    FileUploadOptions: any = null;
    FileUploadProgress: any = null;
    BlobId: string = null;
    SpreadsheetValidated: boolean = false;
    SpreadsheetErrors: any[] = [];

    attributeEditorDialog: AttributeEditorDialogViewModel = new AttributeEditorDialogViewModel();

    get AttributesOrdered(): AttributeReadModel[]
    {
        return this.rm.Attributes.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
    }

    get NextToAlternativesAttributesMappingsButtonVisible(): boolean  
    {
        return this.ImportStep === "source"
            && this.SpreadsheetValidated === true
            && this.SpreadsheetErrors.length === 0;
    }

    loadMappings(blobId: string) 
    {
        this.blobId = blobId;
        this.runImportAlternativesPageQueryV1(this.workspaceId, this.projectId, this.alternativesListId, blobId);
    }

    cancelImport()
    {
        this.rm = null;
        this.file = null;
        this.ImportStep = 'source';
    }

    getError(name: string): string | null
    {
        return this.importResult.ValidationErrors.find(a => a.Context === name)?.Error;
    }


    showAttributeCreator(mapping: SpreadsheetColumnToAlternativePropertyMapping)
    {
        this.attributeEditorDialog.showCreator({
            defaultName: mapping.ColumnName,
            defaultSection: "Imported",

            allowChangeAttributeType: true,
            allowInputTypeEvaluate: false,
            allowMultipleValuesEnabled: true,
        });


        this.runAttributeEditorQueryV1(this.workspaceId, this.projectId, null);
    }

    loadAttribute(attrId: number)
    {
        this.runAttributeByIdQueryV1(this.workspaceId, this.projectId, this.rm.AlternativesListId, attrId);
    }


    handleImportAlternativesFromSpreadsheetCommand()
    {
        this.Processing = true;

        const mappings = this.rm.Mappings.filter(a => a.PropertyName !== 'None');

        this.invokeImportAlternativesFromSpreadsheetCommandV1(this.rm.UserId, this.workspaceId, this.projectId, this.alternativesListId, this.blobId, this.UpdateExisting, mappings);
    }

    handleDownloadEmptyAlternativesSpreadsheetCommand()
    {
        this.invokeDownloadEmptyAlternativesSpreadsheetCommandV1(this.workspaceId, this.projectId, this.alternativesListId);
    }

    mounted()
    {
        document.title = "Import | TransparentChoice";
    }

    onSetModel(model: any, modelName: string) 
    {
        if (modelName === "ImportAlternativesPageReadModel")
        {
            this.rm = model as ImportAlternativesPageReadModel;

            if (this.rm.HasErrors === false)
            {
                this.ImportStep = 'mappings';
                this.SpreadsheetValidated = true;
            }
            else
            {
                this.SpreadsheetErrors = this.rm.Errors.map(e => e.Error);
            }
        }
        else if (modelName === "AttributeReadModel")
        {
            const attr = model as AttributeReadModel;
            if (attr.IsCalculated === true || this.rm.Attributes.some(a => a.Id === attr.Id)) return;

            this.rm.Attributes.push(attr);

            const column = this.rm.Mappings.find(a => a.ColumnName === attr.Name);

            if (column)
            {
                column.PropertyName = attr.Name;
            }
        }
        else if (modelName === "AttributeEditorReadModel")
        {
            const rm = model as AttributeEditorReadModel;
            this.attributeEditorDialog.setData(rm);
        }
    }

    onCommandSucceed(cmd: any, correlationId: string, result: any)
    {
        if (ImportAlternativesFromSpreadsheetCommandV1.matchType(cmd))
        {
            this.importResult = result;
            this.ImportStep = "summary";
            this.ImportCompleted = true;
        }
        else if (DownloadEmptyAlternativesSpreadsheetCommandV1.matchType(cmd))
        {
            location.href = result;
        }
    }

    onCommandFailed(status: any)
    {
        if (ImportAlternativesFromSpreadsheetCommandV1.matchType(status.Command))
        {
            this.rm.Mappings.forEach(a => a.Error = null);

            for (const error of status.ValidationErrors)
            {
                const xlsColumnName: string = error.Context;
                const mapping = this.rm.Mappings.find(a => a.ColumnName === xlsColumnName);
                mapping.Error = error.Error;
            }

            this.Processing = false;
        }
    }
}

class ImportAlternativesFromSpreadsheetResult 
{
    Created: string[];
    Updated: string[];
    NotChanged: string[];
    FailedToCreate: string[];
    FailedToUpdate: string[];
    ValidationErrors: ValidationError[];
}